// Utility Functions

import { STORAGE_KEYS, SURVEY, VITAL_SIGNS, RISK_LEVELS } from '../constants/app';
import type { Patient, VitalReading } from '../types';

// Local Storage Utilities
export const storage = {
  get: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  
  set: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Handle storage errors silently
    }
  },
  
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Handle storage errors silently
    }
  },
  
  clear: (): void => {
    try {
      localStorage.clear();
    } catch {
      // Handle storage errors silently
    }
  }
};

// Authentication Utilities
export const auth = {
  isLoggedIn: (): boolean => {
    return storage.get(STORAGE_KEYS.IS_LOGGED_IN) === 'true';
  },
  
  getUserEmail: (): string => {
    return storage.get(STORAGE_KEYS.USER_EMAIL) || '';
  },
  
  getUserId: (): string => {
    return storage.get(STORAGE_KEYS.USER_ID) || '';
  },
  
  isAdmin: (): boolean => {
    return storage.get(STORAGE_KEYS.IS_ADMIN) === 'true';
  },
  
  getHospitalName: (): string => {
    return storage.get(STORAGE_KEYS.HOSPITAL_NAME) || '';
  },
  
  saveUserSession: (email: string, isAdmin: boolean, hospitalName: string, userId: string): void => {
    storage.set(STORAGE_KEYS.IS_LOGGED_IN, 'true');
    storage.set(STORAGE_KEYS.USER_EMAIL, email);
    storage.set(STORAGE_KEYS.IS_ADMIN, isAdmin.toString());
    storage.set(STORAGE_KEYS.HOSPITAL_NAME, hospitalName);
    storage.set(STORAGE_KEYS.USER_ID, userId);
  },
  
  clearUserSession: (): void => {
    Object.values(STORAGE_KEYS).forEach(key => storage.remove(key));
  }
};

// Validation Utilities
export const validation = {
  isValidEmail: (email: string): boolean => {
    return SURVEY.VALIDATION.EMAIL_REGEX.test(email);
  },
  
  isValidIndianMobile: (mobile: string): boolean => {
    return SURVEY.VALIDATION.MOBILE_REGEX.test(mobile);
  },
  
  isRequired: (value: string): boolean => {
    return value.trim().length > 0;
  },
  
  hasMinLength: (value: string, minLength: number): boolean => {
    return value.length >= minLength;
  },
  
  hasMaxLength: (value: string, maxLength: number): boolean => {
    return value.length <= maxLength;
  },
  
  isNumeric: (value: string): boolean => {
    return /^\d+$/.test(value);
  },
  
  isValidAge: (age: string): boolean => {
    const numAge = parseInt(age);
    return validation.isNumeric(age) && numAge > 0 && numAge <= 120 && age.length <= SURVEY.VALIDATION.AGE_MAX_DIGITS;
  },
  
  isValidWeight: (weight: string): boolean => {
    const numWeight = parseFloat(weight);
    return !isNaN(numWeight) && numWeight > 0 && weight.length <= SURVEY.VALIDATION.WEIGHT_MAX_DIGITS;
  }
};

// Conversion Utilities
export const conversions = {
  feetInchesToCm: (feet: number, inches: number): number => {
    return (feet * SURVEY.CONVERSIONS.CM_PER_FOOT) + (inches * SURVEY.CONVERSIONS.CM_PER_INCH);
  },
  
  poundsToKg: (pounds: number): number => {
    return pounds * SURVEY.CONVERSIONS.KG_PER_POUND;
  },
  
  calculateBMI: (heightCm: number, weightKg: number): number => {
    const heightM = heightCm / 100;
    return Math.round((weightKg / (heightM * heightM)) * 10) / 10;
  },
  
  formatHeight: (heightCm: number): string => {
    const feet = Math.floor(heightCm / SURVEY.CONVERSIONS.CM_PER_FOOT);
    const inches = Math.round((heightCm % SURVEY.CONVERSIONS.CM_PER_FOOT) / SURVEY.CONVERSIONS.CM_PER_INCH);
    return `${feet}'${inches}"`;
  },
  
  formatWeight: (weightKg: number): string => {
    return `${Math.round(weightKg * 10) / 10} kg`;
  }
};

// Date and Time Utilities
export const dateTime = {
  formatTimestamp: (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      return `${displayHours}:${displayMinutes} ${ampm}`;
    } catch {
      return 'Invalid Date';
    }
  },
  
  formatDate: (date: Date | string): string => {
    try {
      const d = new Date(date);
      return d.toLocaleDateString(undefined, { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch {
      return 'Invalid Date';
    }
  },
  
  formatDateTime: (date: Date | string): string => {
    try {
      const d = new Date(date);
      return `${dateTime.formatDate(d)}, ${dateTime.formatTimestamp(d.toISOString())}`;
    } catch {
      return 'Invalid Date';
    }
  },
  
  isToday: (date: Date | string): boolean => {
    try {
      const d = new Date(date);
      const today = new Date();
      return d.toDateString() === today.toDateString();
    } catch {
      return false;
    }
  },
  
  getAge: (dob: string): number => {
    try {
      let day: number, month: number, year: number;
      
      if (dob.includes('-')) {
        const parts = dob.split('-');
        if (parts.length === 3) {
          year = parseInt(parts[0]);
          month = parseInt(parts[1]);
          day = parseInt(parts[2]);
        } else {
          return 0;
        }
      } else {
        const parts = dob.split('/');
        if (parts.length === 3) {
          day = parseInt(parts[0]);
          month = parseInt(parts[1]);
          year = parseInt(parts[2]);
        } else {
          return 0;
        }
      }
      
      if (!year) return 0;
      const today = new Date();
      let age = today.getFullYear() - year;
      if (
        today.getMonth() + 1 < month ||
        (today.getMonth() + 1 === month && today.getDate() < day)
      ) {
        age--;
      }
      return age;
    } catch {
      return 0;
    }
  }
};

// Patient Data Utilities
export const patientUtils = {
  getFullName: (patient: Patient): string => {
    return patient.personal_information?.full_name || 'Unknown';
  },
  
  getAge: (patient: Patient): number => {
    return dateTime.getAge(patient.personal_information?.dob || '');
  },
  
  getGender: (patient: Patient): string => {
    return patient.personal_information?.gender || 'Unknown';
  },
  
  getRiskLevel: (patient: Patient): string => {
    return patient.score_summary?.risk_level || 'unknown';
  },
  
  getRiskScore: (patient: Patient): number => {
    return patient.score_summary?.risk_score || 0;
  },
  
  getLatestVital: (patient: Patient, bodyType: string): VitalReading | null => {
    if (!patient.vitals) return null;
    let all: VitalReading[] = [];
    Object.values(patient.vitals).forEach((arr: any) => {
      if (Array.isArray(arr)) {
        all = all.concat(arr.filter((v: any) => v.bodyType === bodyType));
      }
    });
    if (all.length === 0) return null;
    all.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return all[0];
  },
  
  getLatestHeartRate: (patient: Patient): number | null => {
    const vital = patientUtils.getLatestVital(patient, VITAL_SIGNS.HEART_RATE);
    return vital?.body?.value || null;
  },
  
  getLatestBloodPressure: (patient: Patient): { systolic: number; diastolic: number } | null => {
    const vital = patientUtils.getLatestVital(patient, VITAL_SIGNS.BLOOD_PRESSURE);
    if (vital?.body?.systolic && vital?.body?.diastolic) {
      return {
        systolic: vital.body.systolic,
        diastolic: vital.body.diastolic
      };
    }
    return null;
  },
  
  getLatestBloodOxygen: (patient: Patient): number | null => {
    const vital = patientUtils.getLatestVital(patient, VITAL_SIGNS.BLOOD_OXYGEN);
    return vital?.body?.value || null;
  },
  
  filterByHospital: (patients: Patient[], userId: string, isAdmin: boolean): Patient[] => {
    if (isAdmin) return patients;
    return patients.filter(patient => patient.hospital_id === userId);
  }
};

// Vital Signs Utilities
export const vitalUtils = {
  getHeartRateColor: (heartRate: number): string => {
    const { MIN, MAX } = VITAL_SIGNS.RANGES.HEART_RATE;
    if (heartRate < MIN || heartRate > MAX) {
      return 'text-red-500';
    }
    return 'text-green-500';
  },
  
  getBloodPressureColor: (systolic: number, diastolic: number): string => {
    const { SYSTOLIC_HIGH, DIASTOLIC_HIGH, SYSTOLIC_ELEVATED, DIASTOLIC_ELEVATED } = VITAL_SIGNS.RANGES.BLOOD_PRESSURE;
    if (systolic >= SYSTOLIC_HIGH || diastolic >= DIASTOLIC_HIGH) {
      return 'text-red-500';
    }
    if (systolic >= SYSTOLIC_ELEVATED || diastolic >= DIASTOLIC_ELEVATED) {
      return 'text-yellow-500';
    }
    return 'text-green-500';
  },
  
  getBloodOxygenColor: (oxygen: number): string => {
    const { MIN, CRITICAL } = VITAL_SIGNS.RANGES.BLOOD_OXYGEN;
    if (oxygen < CRITICAL) {
      return 'text-red-500';
    }
    if (oxygen < MIN) {
      return 'text-yellow-500';
    }
    return 'text-green-500';
  },
  
  isHeartRateNormal: (heartRate: number): boolean => {
    const { MIN, MAX } = VITAL_SIGNS.RANGES.HEART_RATE;
    return heartRate >= MIN && heartRate <= MAX;
  },
  
  isBloodPressureNormal: (systolic: number, diastolic: number): boolean => {
    const { SYSTOLIC_ELEVATED, DIASTOLIC_ELEVATED } = VITAL_SIGNS.RANGES.BLOOD_PRESSURE;
    return systolic < SYSTOLIC_ELEVATED && diastolic < DIASTOLIC_ELEVATED;
  },
  
  isBloodOxygenNormal: (oxygen: number): boolean => {
    return oxygen >= VITAL_SIGNS.RANGES.BLOOD_OXYGEN.MIN;
  }
};

// Risk Assessment Utilities
export const riskUtils = {
  determineRiskLevel: (score: number): string => {
    const { LOW, MEDIUM } = RISK_LEVELS.THRESHOLDS;
    if (score <= LOW) return RISK_LEVELS.LOW;
    if (score <= MEDIUM) return RISK_LEVELS.MEDIUM;
    return RISK_LEVELS.HIGH;
  },
  
  getRiskBadgeClass: (riskLevel: string): string => {
    switch (riskLevel.toLowerCase()) {
      case RISK_LEVELS.HIGH:
        return 'bg-red-100 text-red-800';
      case RISK_LEVELS.MEDIUM:
        return 'bg-yellow-100 text-yellow-800';
      case RISK_LEVELS.LOW:
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  },
  
  getRiskConfig: (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case RISK_LEVELS.HIGH:
        return {
          logo: '/images/High_logo.png',
          bgColor: '#EF4444',
          message: 'High cardiovascular risk detected. Immediate medical attention recommended.'
        };
      case RISK_LEVELS.MEDIUM:
        return {
          logo: '/images/Medium_logo.png',
          bgColor: '#F59E0B',
          message: 'Moderate cardiovascular risk. Regular monitoring and lifestyle changes recommended.'
        };
      case RISK_LEVELS.LOW:
        return {
          logo: '/images/Low_logo.png',
          bgColor: '#14B8A6',
          message: 'Low cardiovascular risk. Continue maintaining healthy lifestyle.'
        };
      default:
        return {
          logo: '/images/High_logo.png',
          bgColor: '#6B7280',
          message: 'Risk assessment unavailable.'
        };
    }
  }
};

// Array Utilities
export const arrayUtils = {
  isEmpty: (arr: any[]): boolean => {
    return !arr || arr.length === 0;
  },
  
  isNotEmpty: (arr: any[]): boolean => {
    return arr && arr.length > 0;
  },
  
  chunk: <T>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  },
  
  unique: <T>(array: T[], keyFn?: (item: T) => any): T[] => {
    if (!keyFn) {
      return Array.from(new Set(array));
    }
    const seen = new Set();
    return array.filter(item => {
      const key = keyFn(item);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  },
  
  sortBy: <T>(array: T[], keyFn: (item: T) => any, direction: 'asc' | 'desc' = 'asc'): T[] => {
    return [...array].sort((a, b) => {
      const aVal = keyFn(a);
      const bVal = keyFn(b);
      if (direction === 'desc') {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    });
  }
};

// String Utilities
export const stringUtils = {
  capitalize: (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  },
  
  truncate: (str: string, length: number, suffix: string = '...'): string => {
    if (str.length <= length) return str;
    return str.slice(0, length) + suffix;
  },
  
  slugify: (str: string): string => {
    return str
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  },
  
  isEmpty: (str: string): boolean => {
    return !str || str.trim().length === 0;
  },
  
  isNotEmpty: (str: string): boolean => {
    return Boolean(str && str.trim().length > 0);
  },
  
  formatName: (firstName: string, lastName: string): string => {
    return [firstName, lastName].filter(Boolean).join(' ');
  },
  
  extractInitials: (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }
};

// API Utilities
export const apiUtils = {
  handleResponse: async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
    }
    return response.json();
  },
  
  createRequestOptions: (method: string, body?: any): RequestInit => {
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    };
    
    if (body) {
      options.body = JSON.stringify(body);
    }
    
    return options;
  },
  
  withTimeout: <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
      )
    ]);
  }
};

// Debounce Utility
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Throttle Utility
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
};

// Error Handling Utilities
export const errorUtils = {
  getErrorMessage: (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'An unknown error occurred';
  },
  
  logError: (error: unknown, context?: string): void => {
    console.error(context ? `[${context}]` : '', error);
  },
  
  isNetworkError: (error: unknown): boolean => {
    if (error instanceof Error) {
      return error.message.toLowerCase().includes('network') ||
             error.message.toLowerCase().includes('fetch') ||
             error.message.toLowerCase().includes('timeout');
    }
    return false;
  }
};
